/**
 * Zod input schemas: signatures. Split out of the one big schemas.ts (Oct 2026) so a session opens only the tool it
 * works on; nothing changed in the schemas themselves. Import as before, from '@daaily/shared' (or './schemas.js'
 * inside shared); the barrel re-exports every file in this folder. Shared primitives (EmailSchema, MonthSchema, …)
 * live in primitives.ts; cross-tool references are explicit imports between these files.
 */
import { z } from 'zod';
/** Signatures tool — a field placed on the document (normalized top-left coords). */
export const SignatureFieldSchema = z.object({
    id: z.string().min(1).max(40),
    signerEmail: z.string().trim().toLowerCase().email(),
    type: z.enum(['signature', 'initials', 'name', 'email', 'date', 'title', 'location', 'text', 'stamp']),
    page: z.number().int().min(0).max(500),
    x: z.number().min(0).max(1),
    y: z.number().min(0).max(1),
    w: z.number().gt(0).max(1),
    h: z.number().gt(0).max(1),
    label: z.string().trim().max(120).nullable().optional(),
    /** type 'stamp' only: which of the sender's saved company stamps (1|2). */
    stampIndex: z.union([z.literal(1), z.literal(2)]).nullish(),
    /** type 'stamp': the company stamp id (Signatures → Stamps). */
    stampId: z.string().min(1).max(40).nullish(),
});
/** Signatures tool — create a request (PDF travels base64; size re-checked server-side). */
export const SignatureCreateRequestSchema = z.object({
    title: z.string().trim().min(1).max(200),
    message: z.string().trim().max(2000).nullable().optional(),
    /** ISO YYYY-MM-DD, informational. */
    dueDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().optional(),
    filename: z.string().trim().min(1).max(200),
    fileBase64: z.string().min(1),
    /** ORDER MATTERS when `ordered` — signers sign in this sequence. */
    signerEmails: z.array(z.string().trim().toLowerCase().email()).min(1).max(10),
    ordered: z.boolean().optional(),
    /** @deprecated Old disable-certificate flag — kept for schema compat. Ignored
     *  on new requests (use auditTrailSeparate instead). */
    includeCertificate: z.boolean().optional(),
    /** When true: the signed PDF has no certificate pages; a separate audit-trail
     *  PDF is stored and filed only for the sender. Default false. */
    auditTrailSeparate: z.boolean().optional(),
    fields: z.array(SignatureFieldSchema).max(100).optional(),
    /** Vault category for the signed copies (validated vs config server-side). */
    vaultCategory: z.string().trim().min(1).max(80).optional(),
    /** "Who gets a copy" — non-signatories who receive the signed PDF in their
     *  vault on completion. (.nullish: httpsCallable serializes undefined→null.) */
    copyRecipientEmails: z.array(z.string().trim().toLowerCase().email()).max(10).nullish(),
    /** Per-signer options (Employee/Company slots): vault category override for
     *  that signer's filed copy + an informational role label. Emails must be a
     *  subset of signerEmails. (.nullish for callable serialization.) */
    signerOptions: z
        .array(z.object({
        email: z.string().trim().toLowerCase().email(),
        vaultCategory: z.string().trim().min(1).max(80).nullish(),
        role: z.enum(['employee', 'company']).nullish(),
    }))
        .max(10)
        .nullish(),
    /** Every signer confirms with a text-message code (always on for the "Contract" category). */
    requireSms: z.boolean().nullish(),
});
export const SignatureAuditTrailUrlSchema = z.object({
    requestId: z.string().min(1),
    download: z.boolean().optional(),
});
export const SignatureSignSchema = z.object({
    requestId: z.string().regex(/^[A-Za-z0-9_-]{1,64}$/, 'Invalid request id.'),
    /** PNG/JPEG data (base64, no data: prefix). */
    imageBase64: z.string().min(1),
    /** Explicit consent to sign electronically — must be true. */
    consent: z.literal(true),
    /** Values for the signer's TEXT fields (fieldId → value). */
    textValues: z.record(z.string().trim().max(300)).optional(),
    /** Custom INITIALS image (PNG/JPEG base64) — only when the doc has an
     *  initials field for this signer and they didn't use the auto default. */
    initialsImageBase64: z.string().optional(),
    /** Also save this signature image as the signer's reusable profile signature. */
    saveSignatureToProfile: z.boolean().optional(),
    /** Firebase ID token of the text-message confirmation, when the request requires one. */
    phoneIdToken: z.string().max(5000).nullish(),
});
export const SignatureDeclineSchema = z.object({
    requestId: z.string().min(1),
    reason: z.string().trim().min(1).max(1000),
});
export const SignatureRequestIdSchema = z.object({ requestId: z.string().regex(/^[A-Za-z0-9_-]{1,64}$/, 'Invalid request id.') });
/** Voiding requires a reason (mirrors decline). */
export const SignatureCancelSchema = z.object({
    requestId: z.string().min(1),
    reason: z.string().trim().min(1).max(1000),
});
export const SignatureDownloadUrlSchema = z.object({
    requestId: z.string().min(1),
    kind: z.enum(['source', 'signed']),
    // nullish: the callable serializer sends undefined as null on the wire.
    // Absent/null/false = INLINE (view); true = attachment (download).
    download: z.boolean().nullish(),
});
// ── Signature Templates ──────────────────────────────────────────────────────
/** A normalized placeholder region on the template PDF. */
export const TemplatePlaceholderSchema = z.object({
    id: z.string().min(1).max(40),
    label: z.string().trim().min(1).max(120),
    page: z.number().int().min(0).max(500),
    x: z.number().min(0).max(1),
    y: z.number().min(0).max(1),
    w: z.number().gt(0).max(1),
    h: z.number().gt(0).max(1),
    fontSize: z.number().min(6).max(72),
    source: z.enum(['manual', 'employeeName', 'employeeJobTitle', 'today']),
});
/** A SignatureField without a signerEmail — the role owns the field set. */
export const TemplateFieldSchema = z.object({
    id: z.string().min(1).max(40),
    type: z.enum(['signature', 'initials', 'name', 'email', 'date', 'title', 'location', 'text', 'stamp']),
    page: z.number().int().min(0).max(500),
    x: z.number().min(0).max(1),
    y: z.number().min(0).max(1),
    w: z.number().gt(0).max(1),
    h: z.number().gt(0).max(1),
    label: z.string().trim().max(120).nullable().optional(),
});
export const TemplateSignerRoleSchema = z.object({
    id: z.string().min(1).max(40),
    label: z.string().trim().min(1).max(80),
    fields: z.array(TemplateFieldSchema).max(50),
});
/** Create a template — PDF travels base64 on creation. */
export const SignatureTemplateSaveSchema = z.object({
    // nullish (not just optional): the Firebase callable serializer turns
    // undefined into null on the wire — absent and null must both mean "not set".
    id: z.string().min(1).max(40).nullish(), // absent/null = create, present = update
    name: z.string().trim().min(1).max(200),
    /** Base64 PDF bytes — required only on CREATE (id absent). */
    fileBase64: z.string().nullish(),
    filename: z.string().trim().min(1).max(200).nullish(),
    placeholders: z.array(TemplatePlaceholderSchema).max(50),
    signerRoles: z.array(TemplateSignerRoleSchema).min(1).max(10),
    vaultCategory: z.string().trim().min(1).max(80).nullish(),
    auditTrailSeparate: z.boolean().nullish(),
    /** Requests from this template sign SEQUENTIALLY, in signer-role order. */
    ordered: z.boolean().nullish(),
    /** Sender enablement — absent/null/empty = available to ALL senders.
     *  Entries validated server-side as active hub users. */
    allowedSenderEmails: z.array(z.string().trim().toLowerCase().email()).max(100).nullish(),
});
/** Create (no stampId) or update a company stamp. imageBase64 = raw base64 PNG/JPEG; optional on update. */
export const SignatureStampSaveSchema = z.object({
    stampId: z.string().min(1).max(40).nullish(),
    name: z.string().trim().min(1).max(80),
    imageBase64: z.string().min(1).nullish(),
    allowedEmails: z.array(z.string().trim().toLowerCase().email()).max(200),
});
export const SignatureStampIdSchema = z.object({ stampId: z.string().min(1).max(40) });
/** Delete a template. */
export const SignatureTemplateIdSchema = z.object({ templateId: z.string().min(1) });
/** Get a signed URL for the template PDF. */
export const SignatureTemplateUrlSchema = z.object({
    templateId: z.string().min(1),
    download: z.boolean().optional(),
    asBytes: z.boolean().nullish(),
});
/** Create a request from a template. */
export const SignatureCreateFromTemplateSchema = z.object({
    templateId: z.string().min(1),
    title: z.string().trim().min(1).max(200),
    message: z.string().trim().max(2000).nullable().optional(),
    dueDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().optional(),
    /** Filled placeholder values (placeholderId → text). */
    values: z.record(z.string().trim().max(500)),
    /** Resolved signers per role (roleId → { email, name }). */
    roleSigners: z.record(z.object({
        email: z.string().trim().toLowerCase().email(),
        name: z.string().trim().min(1).max(200),
    })),
    // nullish: the callable serializer sends undefined as null on the wire.
    ordered: z.boolean().nullish(),
    vaultCategory: z.string().trim().min(1).max(80).nullish(),
    auditTrailSeparate: z.boolean().nullish(),
    /** Every signer confirms with a text-message code (always on for the "Contract" category). */
    requireSms: z.boolean().nullish(),
});
//# sourceMappingURL=signatures.js.map